import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Bed, Bathtub, Ruler, Tree, CalendarBlank, Phone, ChatText, EnvelopeSimple, ArrowRight, CheckCircle, House, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";
import { money, coverImage, onPropImgError, fmtDate } from "../lib/helpers";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const OX = "#4A0000";
const GOLD = "#B08D57";

function Stat({ icon: Icon, label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex flex-col items-center text-center px-4 py-3" data-testid={`ll-stat-${label.toLowerCase()}`}>
      <Icon size={22} weight="duotone" color={OX} />
      <span className="font-display text-2xl mt-1 text-[#1A1A1A]">{value}</span>
      <span className="text-[11px] uppercase tracking-widest text-[#8A8782] mt-0.5">{label}</span>
    </div>
  );
}

export default function LeadLander() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("message"); // message | tour
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", tour_date: "", tour_time: "" });
  const [slotId, setSlotId] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    client.get(`/public/property/${id}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) return toast.error("Add an email or phone so the agent can reach you.");
    if (mode === "message" && !form.message.trim()) return toast.error("Please include a short message.");
    if (mode === "tour" && !slotId) return toast.error("Pick an available tour time.");
    setSaving(true);
    try {
      const r = await client.post(`/public/property/${id}/lead`, { ...form, kind: mode, slot_id: mode === "tour" ? slotId : undefined });
      setDone(true);
      toast.success(r.data.message || "Sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send — please try again.");
    } finally { setSaving(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBFAF7] px-6 text-center" data-testid="ll-notfound">
      <House size={48} weight="duotone" color={OX} />
      <h1 className="font-display text-3xl mt-4 text-[#1A1A1A]">Property not found</h1>
      <p className="text-[#5C5A58] mt-2">This listing page isn't available.</p>
    </div>
  );
  if (!p) return <div className="min-h-screen flex items-center justify-center bg-[#FBFAF7] font-display text-2xl text-[#4A0000]" data-testid="ll-loading">Loading…</div>;

  const cityState = [p.city, p.state].filter(Boolean).join(", ");
  const agent = p.agent || {};
  const initials = (agent.name || "R").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const t12 = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${ap}`; };
  const slotsByDate = (p.tour_slots || []).reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#FBFAF7] grain" data-testid="leadlander-page">
      {/* Header */}
      <header className="border-b border-[#E9DDC6] bg-[#FBFAF7]/90 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark logoSize={30} textClass="text-[#4A0000]" textSize="text-2xl" />
          <span className="hidden sm:flex items-center gap-1.5 text-xs text-[#8A6D1B] bg-[#B08D57]/12 border border-[#B08D57]/30 rounded-full px-3 py-1">
            <Crown size={13} weight="fill" /> Presented by Reelty
          </span>
        </div>
      </header>

      {/* Hero */}
      <div className="relative">
        <div className="h-[46vh] min-h-[320px] w-full overflow-hidden bg-[#FAF9F6]">
          <img src={coverImage(p)} alt={p.title} onError={onPropImgError} className="w-full h-full object-cover" data-testid="ll-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#4A0000]/80 via-[#4A0000]/20 to-transparent" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-6 pb-7 text-white">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight drop-shadow" data-testid="ll-headline">
              {p.headline || p.title}
            </h1>
            <p className="flex items-center gap-1.5 mt-2 text-white/90"><MapPin size={17} weight="fill" />{p.address || cityState}</p>
            {p.list_price ? (
              <p className="font-mono-num text-3xl sm:text-4xl mt-3 text-[#F0D9A8]" data-testid="ll-price">{money(p.list_price)}</p>
            ) : null}
          </div>
        </motion.div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        {/* Left / main */}
        <div className="lg:col-span-2 space-y-8">
          {/* Stats */}
          <div className="bg-white border border-[#E9DDC6] rounded-xl flex flex-wrap justify-around divide-x divide-[#F0ECE2] shadow-sm" data-testid="ll-stats">
            <Stat icon={Bed} label="Beds" value={p.beds != null ? p.beds : null} />
            <Stat icon={Bathtub} label="Baths" value={p.baths != null ? p.baths : null} />
            <Stat icon={Ruler} label="Sq Ft" value={p.sqft != null ? Number(p.sqft).toLocaleString() : null} />
            <Stat icon={Tree} label="Acres" value={p.acres != null ? p.acres : null} />
          </div>

          {/* Description */}
          {p.description && (
            <section data-testid="ll-description">
              <h2 className="font-display text-2xl text-[#1A1A1A] mb-2">About this home</h2>
              <p className="text-[#5C5A58] leading-relaxed whitespace-pre-line">{p.description}</p>
            </section>
          )}

          {/* Open houses */}
          {(p.open_houses || []).length > 0 && (
            <section data-testid="ll-open-houses">
              <h2 className="font-display text-2xl text-[#1A1A1A] mb-3">Open houses</h2>
              <div className="space-y-2">
                {p.open_houses.map((oh, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-[#E9DDC6] rounded-lg px-4 py-3 shadow-sm">
                    <CalendarBlank size={20} weight="duotone" color={GOLD} />
                    <span className="font-medium text-[#1A1A1A]">{fmtDate(oh.date)}</span>
                    {(oh.start || oh.end) && <span className="text-sm text-[#5C5A58]">{[oh.start, oh.end].filter(Boolean).join(" – ")}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right / contact rail */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 lux-card rounded-[8px] p-6 space-y-5" data-testid="ll-contact-card">
            <div className="flex items-center gap-3">
              {agent.avatar
                ? <img src={agent.avatar} alt={agent.name} className="w-14 h-14 rounded-full object-cover border border-[#E9DDC6]" />
                : <div className="w-14 h-14 rounded-full bg-[#4A0000] text-white flex items-center justify-center font-display text-lg">{initials}</div>}
              <div>
                <p className="font-medium text-[#1A1A1A]" data-testid="ll-agent-name">{agent.name || "Listing Agent"}</p>
                {agent.company && <p className="text-sm text-[#8A8782]">{agent.company}</p>}
              </div>
            </div>

            {/* Call / Text / Email */}
            <div className="grid grid-cols-3 gap-2">
              <a data-testid="ll-call" href={agent.phone ? `tel:${agent.phone}` : undefined}
                aria-disabled={!agent.phone}
                className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-medium transition ${agent.phone ? "border-[#4A0000] text-[#4A0000] hover:bg-[#4A0000] hover:text-white" : "border-[#E9DDC6] text-[#C9C4BA] pointer-events-none"}`}>
                <Phone size={18} weight="fill" /> Call
              </a>
              <a data-testid="ll-text" href={agent.phone ? `sms:${agent.phone}` : undefined}
                aria-disabled={!agent.phone}
                className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-medium transition ${agent.phone ? "border-[#4A0000] text-[#4A0000] hover:bg-[#4A0000] hover:text-white" : "border-[#E9DDC6] text-[#C9C4BA] pointer-events-none"}`}>
                <ChatText size={18} weight="fill" /> Text
              </a>
              <a data-testid="ll-email" href={agent.email ? `mailto:${agent.email}` : undefined}
                aria-disabled={!agent.email}
                className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-medium transition ${agent.email ? "border-[#4A0000] text-[#4A0000] hover:bg-[#4A0000] hover:text-white" : "border-[#E9DDC6] text-[#C9C4BA] pointer-events-none"}`}>
                <EnvelopeSimple size={18} weight="fill" /> Email
              </a>
            </div>

            <div className="border-t border-[#F0ECE2] pt-5">
              {done ? (
                <div className="text-center py-6" data-testid="ll-lead-success">
                  <CheckCircle size={44} weight="fill" color="#2E7D5B" className="mx-auto" />
                  <p className="font-display text-xl mt-3 text-[#1A1A1A]">Message sent</p>
                  <p className="text-sm text-[#5C5A58] mt-1">The listing agent has been notified and will reach out soon.</p>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-4">
                    <button type="button" data-testid="ll-mode-message" onClick={() => setMode("message")}
                      className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${mode === "message" ? "bg-[#4A0000] text-white" : "bg-[#FAF9F6] text-[#5C5A58]"}`}>
                      Ask a question
                    </button>
                    <button type="button" data-testid="ll-mode-tour" onClick={() => setMode("tour")}
                      className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${mode === "tour" ? "bg-[#4A0000] text-white" : "bg-[#FAF9F6] text-[#5C5A58]"}`}>
                      Request a tour
                    </button>
                  </div>
                  <form onSubmit={submit} className="space-y-3" data-testid="ll-lead-form">
                    <div>
                      <Label className="text-xs text-[#5C5A58]">Name</Label>
                      <Input data-testid="ll-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1 border-[#E9DDC6]" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-[#5C5A58]">Email</Label>
                        <Input data-testid="ll-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="mt-1 border-[#E9DDC6]" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#5C5A58]">Phone</Label>
                        <Input data-testid="ll-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" className="mt-1 border-[#E9DDC6]" />
                      </div>
                    </div>
                    {mode === "tour" ? (
                      <div data-testid="ll-tour-slots">
                        <Label className="text-xs text-[#5C5A58]">Choose a tour time</Label>
                        {(p.tour_slots || []).length === 0 ? (
                          <p className="text-xs text-[#8A8782] mt-1.5" data-testid="ll-no-slots">No tour times posted yet — send a message and the agent will arrange one.</p>
                        ) : (
                          <div className="mt-1.5 space-y-2.5 max-h-56 overflow-y-auto pr-1">
                            {Object.entries(slotsByDate).map(([date, list]) => (
                              <div key={date}>
                                <p className="text-[11px] uppercase tracking-wide text-[#8A8782] mb-1 flex items-center gap-1"><CalendarBlank size={12} weight="fill" color={GOLD} />{fmtDate(date)}</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {list.map((s) => (
                                    <button type="button" key={s.id} data-testid={`ll-slot-${s.id}`} onClick={() => setSlotId(s.id)}
                                      className={`px-2.5 py-1.5 rounded-md text-xs border transition ${slotId === s.id ? "bg-[#4A0000] text-white border-[#4A0000]" : "bg-white text-[#4A0000] border-[#E9DDC6] hover:border-[#B08D57]"}`}>
                                      {t12(s.start)}{s.end ? ` – ${t12(s.end)}` : ""}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                    <div>
                      <Label className="text-xs text-[#5C5A58]">Message</Label>
                      <textarea data-testid="ll-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder={mode === "tour" ? "Anything the agent should know? (optional)" : "I'd love to learn more about this home…"}
                        className="mt-1 w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
                    </div>
                    <Button data-testid="ll-lead-submit" type="submit" disabled={saving}
                      className="w-full bg-[#4A0000] hover:bg-[#5C1A1A] text-white rounded-lg py-2.5">
                      {saving ? "Sending…" : <>{mode === "tour" ? "Request tour" : "Send message"} <ArrowRight size={16} className="ml-1" weight="bold" /></>}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>

      <footer className="border-t border-[#E9DDC6] py-8 text-center">
        <p className="text-xs text-[#8A8782]">Marketed with <span className="text-[#4A0000] font-medium">Reelty</span> — premium real estate media.</p>
      </footer>
    </div>
  );
}
